import { useState } from 'react';
import { ArrowDown, Check } from 'lucide-react';
import clsx from 'clsx';

import Sheet from '@/features/chat/components/Sheet';
import { Stepper } from '@/components/ui';
import { formatQty, isFractionalUnit } from '@/lib/format';
import type { VisitSwap } from '@/services/courier';
import { r3 } from './visit-math';

export interface ExchangeFrom {
  id: string;
  name: string;
  unit: string;
  /** Сколько ещё можно забрать; null — не знаем (нет связи). */
  left: number | null;
}

export interface ExchangeTo {
  id: string;
  name: string;
  unit: string;
  /** Сколько ещё в машине; null — без накладной, не ограничено. */
  left: number | null;
}

/**
 * Обмен: точка отдаёт одно, получает другое. Деньги не двигаются — забранное
 * спишется, выданное уйдёт как потеря по себестоимости.
 */
export function ExchangeSheet({
  from,
  to,
  onAdd,
  onClose,
}: {
  from: ExchangeFrom[];
  to: ExchangeTo[];
  onAdd: (s: VisitSwap) => void;
  onClose: () => void;
}) {
  const [fromId, setFromId] = useState<string | null>(from.length === 1 ? from[0]!.id : null);
  const [qty, setQty] = useState(0);
  const [toId, setToId] = useState<string | null>(null);
  const [toQty, setToQty] = useState<number | null>(null);
  const f = from.find((x) => x.id === fromId) ?? null;
  const t = to.find((x) => x.id === toId && x.id !== fromId) ?? null;
  const outQty = toQty ?? qty;
  const valid = !!f && !!t && qty > 0 && outQty > 0 && (f.left == null || qty <= f.left + 1e-9) && (t.left == null || outQty <= t.left + 1e-9);

  return (
    <Sheet title="Обмен товара" onClose={onClose}>
      <p className="-mt-2 text-[13px] text-ink/55">Деньги не меняются: забранное спишем, выданное взамен — из машины.</p>

      <h3 className="mt-4 text-[13px] font-medium text-ink/60">Забрал у точки</h3>
      <ul className="mt-1.5 max-h-56 divide-y divide-line overflow-y-auto rounded-xl ring-1 ring-line">
        {from.map((x) => (
          <Option
            key={x.id}
            active={x.id === fromId}
            name={x.name}
            hint={x.left == null ? undefined : `можно ${formatQty(x.left)} ${x.unit}`}
            onPick={() => {
              setFromId(x.id);
              setQty(0);
              if (toId === x.id) setToId(null);
            }}
          />
        ))}
      </ul>
      {f && (
        <div className="mt-2">
          <Stepper label={`${f.name}: забрал`} value={qty} max={f.left ?? undefined} fractional={isFractionalUnit(f.unit)} tone="return" onChange={setQty} />
        </div>
      )}

      <div className="my-3 flex justify-center text-ink/30">
        <ArrowDown size={18} />
      </div>

      <h3 className="text-[13px] font-medium text-ink/60">Отдал взамен</h3>
      <ul className="mt-1.5 max-h-56 divide-y divide-line overflow-y-auto rounded-xl ring-1 ring-line">
        {to
          .filter((x) => x.id !== fromId)
          .map((x) => (
            <Option
              key={x.id}
              active={x.id === toId}
              disabled={x.left != null && x.left <= 0}
              name={x.name}
              hint={x.left == null ? undefined : x.left > 0 ? `в машине ${formatQty(x.left)} ${x.unit}` : 'нет в машине'}
              onPick={() => {
                setToId(x.id);
                setToQty(null);
              }}
            />
          ))}
      </ul>
      {t && (
        <div className="mt-2">
          <Stepper
            label={`${t.name}: отдал взамен`}
            value={outQty}
            max={t.left ?? undefined}
            fractional={isFractionalUnit(t.unit)}
            onChange={setToQty}
          />
        </div>
      )}

      <button
        type="button"
        className="btn-primary mt-5 w-full"
        disabled={!valid}
        onClick={() => {
          if (!f || !t) return;
          onAdd({ fromItemId: f.id, qty: r3(qty), toItemId: t.id, toQty: r3(outQty) });
          onClose();
        }}
      >
        Добавить обмен
      </button>
    </Sheet>
  );
}

function Option({ active, disabled, name, hint, onPick }: { active: boolean; disabled?: boolean; name: string; hint?: string; onPick: () => void }) {
  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        aria-pressed={active}
        className={clsx('flex w-full items-center gap-3 px-3 py-2.5 text-left text-[14px]', active ? 'bg-orange-50' : 'active:bg-ink/[0.03]', disabled && 'opacity-40')}
        onClick={onPick}
      >
        <span className={clsx('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2', active ? 'border-orange bg-orange text-white' : 'border-ink/20')}>
          {active && <Check size={12} strokeWidth={3} />}
        </span>
        <span className="min-w-0 flex-1 text-ink">{name}</span>
        {hint && <span className="shrink-0 text-[12px] tabular-nums text-ink/45">{hint}</span>}
      </button>
    </li>
  );
}
